import { X, Copy, AlertTriangle, ImageIcon, Paintbrush, ChevronDown, ChevronUp, Layers, ArrowRight } from 'lucide-react';
import { useState } from 'react';
import type { ImageJob, FallbackTrace } from './types';
import { LazyImage } from '../LazyImage';
import { formatBeijingDateTime } from '../../utils/time';
import { resolveBrowserMediaUrl } from '../../utils/storageUrl';
import FallbackTraceTimeline from './FallbackTraceTimeline';

interface ImageDetailModalProps {
  image: ImageJob;
  onClose: () => void;
}

const MODE_LABELS: Record<string, string> = {
  text2img: '文生图',
  img2img: '图生图',
  inpaint: '局部重绘',
};

const QUALITY_LABELS: Record<string, string> = {
  low: '标准',
  medium: '高清',
  high: '超清',
};

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  completed: { label: '完成', color: 'text-green-700', bg: 'bg-green-100' },
  processing: { label: '处理中', color: 'text-yellow-700', bg: 'bg-yellow-100' },
  failed: { label: '失败', color: 'text-red-700', bg: 'bg-red-100' },
};

const SOURCE_LABELS: Record<string, string> = {
  web: '网页',
  api: 'API',
};

export default function ImageDetailModal({ image, onClose }: ImageDetailModalProps) {
  const [copied, setCopied] = useState(false);
  const [showFullError, setShowFullError] = useState(false);

  const handleCopyPrompt = () => {
    navigator.clipboard.writeText(image.prompt || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const statusInfo = STATUS_CONFIG[image.status] || STATUS_CONFIG.processing;
  const modeLabel = MODE_LABELS[image.mode || ''] || image.mode || '-';
  const qualityLabel = QUALITY_LABELS[image.quality || ''] || image.quality || '-';
  const sourceLabel = SOURCE_LABELS[image.source || ''] || image.source || '-';
  const sizeDisplay = image.size || '-';
  const referenceImageUrls = (image.referenceImages || []).map((url) => resolveBrowserMediaUrl(url));
  const maskImageUrl = resolveBrowserMediaUrl(image.maskImage);
  const resultImageUrl = resolveBrowserMediaUrl(image.resultUrl);

  // 判断是否为图生图或局部重绘模式
  const isImg2ImgOrInpaint = image.mode === 'img2img' || image.mode === 'inpaint';
  const hasReferenceImages = referenceImageUrls.length > 0;

  const errorSummary = (() => {
    if (!image.errorMessage) return '';
    try {
      const parsed = JSON.parse(image.errorMessage);
      const msg = typeof parsed.message === 'string' ? parsed.message : '生成失败';
      return parsed.httpStatus ? `${msg} (${parsed.httpStatus})` : msg;
    } catch {
      return image.errorMessage.length > 100 ? image.errorMessage.slice(0, 100) + '...' : image.errorMessage;
    }
  })();

  const fullErrorMessage = (() => {
    if (!image.errorMessage) return '';
    try {
      return JSON.stringify(JSON.parse(image.errorMessage), null, 2);
    } catch {
      return image.errorMessage;
    }
  })();

  const userFacingErrorMessage = image.userFacingErrorMessage?.trim() || '';

  const parsedFallbackTrace = (() => {
    if (!image.fallbackTrace) return null;
    try {
      return JSON.parse(image.fallbackTrace) as FallbackTrace;
    } catch {
      return null;
    }
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] sm:max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.color}`}>
              {statusInfo.label}
            </div>
            <h2 className="text-lg font-semibold text-gray-800 truncate">
              图片详情 · {image.id}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6">
            {/* Left: Images */}
            <div className="space-y-4">
              {/* 图生图/局部重绘：展示对比布局 */}
              {isImg2ImgOrInpaint && hasReferenceImages ? (
                <div className="space-y-3">
                  <div className="text-sm font-medium text-gray-500">
                    {image.mode === 'inpaint' ? '原始图 + 蒙版 → 生成图' : '原始图 → 生成图'}
                  </div>
                  <div className="flex flex-col md:flex-row items-center gap-5 justify-center">
                    {/* 原始参考图 */}
                    <div className="relative">
                      <a
                        href={referenceImageUrls[0]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-56 h-56 md:w-52 md:h-52 bg-gray-100 rounded-xl overflow-hidden border-2 border-gray-200 hover:border-blue-400 transition-colors cursor-zoom-in"
                        title="在新标签页打开原始图"
                      >
                        <img
                          src={referenceImageUrls[0]}
                          alt="原始参考图"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                            (e.target as HTMLImageElement).parentElement!.innerHTML = 
                              '<div class="h-full flex items-center justify-center text-gray-400 text-xs">加载失败</div>';
                          }}
                        />
                      </a>
                      {referenceImageUrls.length > 1 && (
                        <div className="absolute -top-1.5 -right-1.5 bg-blue-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                          +{referenceImageUrls.length - 1}
                        </div>
                      )}
                      <div className="text-center mt-1 text-xs text-gray-500">原始图</div>
                    </div>

                    {/* 箭头 */}
                    <div className="flex flex-col items-center gap-1">
                      <ArrowRight className="w-5 h-5 text-blue-500 rotate-90 md:rotate-0" />
                      {/* 局部重绘时显示蒙版缩略图 */}
                      {image.mode === 'inpaint' && maskImageUrl && (
                        <a
                          href={maskImageUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="在新标签页打开蒙版"
                          className="block"
                        >
                          <img
                            src={maskImageUrl}
                            alt="蒙版"
                            className="w-16 h-16 rounded border border-gray-200 object-cover opacity-80 hover:opacity-100 transition-opacity cursor-zoom-in"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </a>
                      )}
                    </div>

                    {/* 生成结果 */}
                    <div className="relative">
                      <div className="w-56 h-56 md:w-52 md:h-52 bg-gray-100 rounded-xl overflow-hidden border-2 border-gray-200">
                        {image.status === 'completed' && resultImageUrl ? (
                          <a
                            href={resultImageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-full h-full hover:opacity-95 transition-opacity cursor-zoom-in"
                            title="点击查看原图（新标签页打开）"
                          >
                            <LazyImage
                              work={image}
                              size="medium"
                              alt="生成结果"
                              className="w-full h-full object-cover"
                            />
                          </a>
                        ) : (
                          <div className="h-full flex items-center justify-center">
                            <div className="text-center">
                              <ImageIcon className="w-8 h-8 text-gray-300 mx-auto mb-1" />
                              <p className="text-xs text-gray-400">
                                {image.status === 'failed' ? '失败' : '处理中'}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="text-center mt-1 text-xs text-gray-500">生成结果</div>
                    </div>
                  </div>

                  {/* 多张参考图时显示缩略图列表 */}
                  {referenceImageUrls.length > 1 && (
                    <div className="pt-2">
                      <div className="text-xs text-gray-500 mb-2">所有参考图</div>
                      <div className="flex gap-2 justify-center flex-wrap">
                        {referenceImageUrls.map((ref, idx) => (
                          <div key={idx} className="relative">
                            <a
                              href={ref}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={`在新标签页打开参考图 ${idx + 1}`}
                              className="block"
                            >
                              <img
                                src={ref}
                                alt={`参考图 ${idx + 1}`}
                                className="w-24 h-24 rounded-lg border border-gray-200 object-cover hover:border-blue-400 transition-colors cursor-zoom-in"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            </a>
                            {idx === 0 && (
                              <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-[8px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                                主
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 蒙版图（局部重绘） */}
                  {image.mode === 'inpaint' && maskImageUrl && (
                    <div className="pt-2">
                      <div className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                        <Paintbrush className="w-3 h-3" />
                        蒙版图（白色区域为保留部分）
                      </div>
                      <div className="bg-gray-100 rounded-lg overflow-hidden max-w-sm mx-auto">
                        <a
                          href={maskImageUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block hover:opacity-95 transition-opacity cursor-zoom-in"
                          title="在新标签页打开蒙版图"
                        >
                          <img
                            src={maskImageUrl}
                            alt="蒙版图"
                            className="w-full h-auto object-contain max-h-72"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                              (e.target as HTMLImageElement).parentElement!.innerHTML = 
                                '<div class="h-32 flex items-center justify-center text-gray-400 text-xs">加载失败</div>';
                            }}
                          />
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* 文生图：只展示结果图 */
                <div className="bg-gray-50 rounded-2xl overflow-hidden">
                  {image.status === 'completed' && resultImageUrl ? (
                    <a
                      href={resultImageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block hover:opacity-95 transition-opacity cursor-zoom-in"
                      title="点击查看原图（新标签页打开）"
                    >
                      <LazyImage
                        work={image}
                        size="medium"
                        alt={image.prompt}
                        className="w-full h-auto object-contain"
                      />
                    </a>
                  ) : (
                    <div className="aspect-square flex items-center justify-center bg-gray-100">
                      <div className="text-center">
                        <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-400">
                          {image.status === 'failed' ? '生成失败' : '处理中...'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right: Info */}
            <div className="space-y-4">
              {/* Prompt Section */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-800">提示词</h3>
                  <button
                    onClick={handleCopyPrompt}
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                  >
                    <Copy className="w-3 h-3" />
                    {copied ? '已复制' : '复制'}
                  </button>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl text-sm text-gray-700 whitespace-pre-wrap max-h-40 overflow-y-auto break-words">
                  {image.prompt}
                </div>
              </div>

              {/* Error Message (for failed images) */}
              {image.status === 'failed' && (image.userFacingErrorMessage || image.errorMessage) && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-red-600 flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" />
                      错误信息
                    </h3>
                    <button
                      onClick={() => setShowFullError(!showFullError)}
                      className="text-xs text-gray-500 flex items-center gap-1 hover:text-gray-700"
                    >
                      {showFullError ? (
                        <>
                          收起 <ChevronUp className="w-3 h-3" />
                        </>
                      ) : (
                        <>
                          完整详情 <ChevronDown className="w-3 h-3" />
                        </>
                      )}
                    </button>
                  </div>
                  <div className="p-4 bg-red-50 rounded-xl">
                    {userFacingErrorMessage && (
                      <div className="mb-3 rounded-lg border border-red-200 bg-white/70 p-3">
                        <div className="mb-1 text-xs font-medium text-red-500">用户侧最终报错</div>
                        <p className="text-sm font-medium text-red-700">{userFacingErrorMessage}</p>
                      </div>
                    )}
                    {image.errorMessage && (
                      <p className="text-sm text-red-700 font-medium">{errorSummary}</p>
                    )}
                    {showFullError && fullErrorMessage && (
                      <pre className="mt-3 text-xs text-red-600 whitespace-pre-wrap break-words max-h-60 overflow-y-auto border-t border-red-200 pt-3">
                        {fullErrorMessage}
                      </pre>
                    )}
                  </div>
                </div>
              )}

              {image.fallbackTrace && (
                <div className="space-y-2">
                  <h3 className="font-medium text-amber-700 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    降级过程
                  </h3>
                  {parsedFallbackTrace ? (
                    <div className="p-3 sm:p-4 bg-amber-50/40 rounded-xl border border-amber-100">
                      <FallbackTraceTimeline trace={parsedFallbackTrace} />
                    </div>
                  ) : (
                    <div className="p-3 sm:p-4 bg-amber-50 rounded-xl">
                      <p className="text-sm text-amber-800 font-medium">降级记录解析失败</p>
                      <pre className="mt-2 text-xs text-amber-700 whitespace-pre-wrap break-words max-h-40 overflow-y-auto">
                        {image.fallbackTrace}
                      </pre>
                    </div>
                  )}
                </div>
              )}

              {/* Generation Parameters */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 bg-gray-50 rounded-xl">
                  <div className="text-gray-500 text-xs mb-1">生成模式</div>
                  <div className="font-medium">{modeLabel}</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-xl">
                  <div className="text-gray-500 text-xs mb-1">模型</div>
                  <div className="font-medium truncate" title={image.model}>{image.model}</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-xl">
                  <div className="text-gray-500 text-xs mb-1">尺寸</div>
                  <div className="font-medium">{sizeDisplay}</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-xl">
                  <div className="text-gray-500 text-xs mb-1">质量</div>
                  <div className="font-medium">{qualityLabel}</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-xl">
                  <div className="text-gray-500 text-xs mb-1">来源</div>
                  <div className="font-medium">{sourceLabel}</div>
                </div>
                {image.channelName && (
                  <div className="p-3 bg-gray-50 rounded-xl">
                    <div className="text-gray-500 text-xs mb-1">API 渠道</div>
                    <div className="font-medium truncate" title={image.channelName}>{image.channelName}</div>
                  </div>
                )}
                <div className="p-3 bg-gray-50 rounded-xl">
                  <div className="text-gray-500 text-xs mb-1">积分消耗</div>
                  <div className="font-medium">{image.creditsUsed}</div>
                </div>
                {image.generation && image.generation > 1 && (
                  <div className="p-3 bg-gray-50 rounded-xl">
                    <div className="text-gray-500 text-xs mb-1">迭代次数</div>
                    <div className="font-medium flex items-center gap-1">
                      <Layers className="w-3 h-3" />
                      #{image.generation}
                    </div>
                  </div>
                )}
              </div>

              {/* User Info */}
              <div className="space-y-2">
                <h3 className="font-medium text-gray-800">用户信息</h3>
                <div className="p-4 bg-gray-50 rounded-xl space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">邮箱</span>
                    <span className="text-gray-800 font-medium">{image.userEmail}</span>
                  </div>
                  {image.userName && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">用户名</span>
                      <span className="text-gray-800">{image.userName}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Timestamps */}
              <div className="p-4 bg-gray-50 rounded-xl text-xs text-gray-500 space-y-1">
                <div>创建时间：{formatBeijingDateTime(image.createdAt)}</div>
                {image.updatedAt && image.updatedAt !== image.createdAt && (
                  <div>更新时间：{formatBeijingDateTime(image.updatedAt)}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
